/**
 * Chained progress persistence with the iOS rollback semantics (AppRootView.onChange(of: progress)):
 * - every mutation enqueues a save after the previous one (mutation order is preserved);
 * - only the latest revision may report: a failing older save is ignored (last write wins);
 * - when the latest save fails, the progress rolls back to the value before that mutation,
 *   keeping `isPremium` when it is still unlocked (rollbackProgressAfterFailedSave).
 */
import type { EpisodeContent, EpisodeProgress } from '@/domain';

export type SaveState = 'idle' | 'saving' | 'failed';
export const SAVE_FAILED_MESSAGE = 'Progress not saved. Check your connection.';

export interface ProgressSaverHooks {
  /** Called with the restored progress; the caller applies it without enqueuing a new save. */
  onRollback(episodeID: string, restored: EpisodeProgress): void;
  isPremiumUnlocked(): boolean;
}

export interface ProgressSaverOptions extends Partial<ProgressSaverHooks> {
  save(userID: string, episode: EpisodeContent, progress: EpisodeProgress): Promise<void>;
  onStateChange(state: SaveState): void;
}

export function rollbackProgressAfterFailedSave(previous: EpisodeProgress, attempted: EpisodeProgress, premiumUnlocked: boolean): EpisodeProgress {
  return attempted.isPremium && premiumUnlocked ? { ...previous, isPremium: true } : previous;
}

export interface ProgressSaver {
  /** `hooks` override the creation-time ones (lets React pass ref-reading closures at call time). */
  enqueue(userID: string, episode: EpisodeContent, previous: EpisodeProgress, next: EpisodeProgress, hooks?: Partial<ProgressSaverHooks>): Promise<void>;
  /** Resolves when every queued save settled (used before switching episodes). */
  flush(): Promise<void>;
  /** Drops the right to roll back for queued saves (episode switch). */
  invalidate(): void;
}

export function createProgressSaver(options: ProgressSaverOptions): ProgressSaver {
  let chain: Promise<void> = Promise.resolve();
  let revision = 0;
  return {
    enqueue(userID, episode, previous, next, hooks = {}) {
      const onRollback = hooks.onRollback ?? options.onRollback;
      const isPremiumUnlocked = hooks.isPremiumUnlocked ?? options.isPremiumUnlocked ?? (() => false);
      revision += 1;
      const rev = revision;
      options.onStateChange('saving');
      chain = chain.then(async () => {
        try {
          await options.save(userID, episode, next);
          if (rev === revision) options.onStateChange('idle');
        } catch {
          if (rev !== revision) return;
          onRollback?.(episode.id, rollbackProgressAfterFailedSave(previous, next, isPremiumUnlocked()));
          options.onStateChange('failed');
        }
      });
      return chain;
    },
    flush: () => chain,
    invalidate() { revision += 1; },
  };
}
